import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { db } from '../lib/firebase';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  getCountFromServer,
} from 'firebase/firestore';

/* ─── Styled components ─── */

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 2rem;
`;

const Btn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 2px solid var(--border);
  background: var(--card-bg);
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: #f87171; color: #ef4444; }
  &[data-liked="true"] { border-color: #ef4444; background: #ef4444; color: #ffffff; }
  &[data-liked="true"]:hover { border-color: #dc2626; background: #dc2626; color: #ffffff; }
  &[data-animating="true"] { transform: scale(1.1); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  svg { width: 1.25rem; height: 1.25rem; transition: transform 0.2s; }
  &[data-animating="true"] svg { transform: scale(1.25); }
`;

const Count = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--text-muted);
  font-size: 0.875rem;
  svg { width: 1.25rem; height: 1.25rem; color: #ef4444; }
  strong { color: var(--text-secondary); font-size: 0.9rem; }
`;

const HEART = 'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z';

/* ─── Helpers ─── */

// Liked articles in localStorage — source of truth for the liked state
const getLikedArticles = () => {
  if (typeof window === 'undefined') return {};
  try {
    const stored = localStorage.getItem('ahl-liked-articles');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveLikedArticles = (likedArticles) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem('ahl-liked-articles', JSON.stringify(likedArticles));
  } catch {
    // Ignore storage errors
  }
};

// Stable per-browser ID to prevent duplicate likes (ahlnew hashes IP + UA server-side)
const getUserId = () => {
  try {
    let id = localStorage.getItem('ahl-like-user-id');
    if (!id) {
      id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('ahl-like-user-id', id);
    }
    return id;
  } catch {
    return 'unknown';
  }
};

const countLikes = async (articleId) => {
  const q = query(collection(db, 'likes'), where('articleId', '==', articleId));
  const snap = await getCountFromServer(q);
  return snap.data().count;
};

/* ─── Component ─── */

const LikeButton = ({ articleId }) => {
  const [likes, setLikes]         = useState(0);
  const [liked, setLiked]         = useState(false);
  const [loading, setLoading]     = useState(true);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    setLiked(!!getLikedArticles()[articleId]);
    fetchLikes();
  }, [articleId]);

  const fetchLikes = async () => {
    try {
      setLikes(await countLikes(articleId));
    } catch (e) {
      console.error('Error fetching likes:', e);
      setLikes(0);
    } finally {
      setLoading(false);
    }
  };

  const toggleLike = async () => {
    const userId = getUserId();
    const likesRef = collection(db, 'likes');
    const existing = await getDocs(query(
      likesRef,
      where('articleId', '==', articleId),
      where('userId', '==', userId)
    ));

    if (!existing.empty) {
      await deleteDoc(doc(db, 'likes', existing.docs[0].id));
    } else {
      await addDoc(likesRef, {
        articleId,
        userId,
        createdAt: new Date().toISOString(),
      });
    }
    return countLikes(articleId);
  };

  const handleLike = async () => {
    if (loading) return;

    // Optimistic update
    const newLiked = !liked;
    const newCount = newLiked ? likes + 1 : Math.max(0, likes - 1);

    setLiked(newLiked);
    setLikes(newCount);
    setAnimating(true);

    const likedArticles = getLikedArticles();
    if (newLiked) {
      likedArticles[articleId] = true;
    } else {
      delete likedArticles[articleId];
    }
    saveLikedArticles(likedArticles);

    setTimeout(() => setAnimating(false), 300);

    try {
      setLikes(await toggleLike());
    } catch (e) {
      console.error('Error toggling like:', e);
      // Keep optimistic update and localStorage state - better UX
    }
  };

  return (
    <Row>
      <Btn
        type="button"
        onClick={handleLike}
        disabled={loading}
        data-liked={liked}
        data-animating={animating}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill={liked ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d={HEART} />
        </svg>
        <span>{liked ? 'Liked' : 'Like'}</span>
      </Btn>

      <Count>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d={HEART} />
        </svg>
        <strong>{loading ? '...' : (likes || 0).toLocaleString()}</strong>
        <span>{likes === 1 ? 'like' : 'likes'}</span>
      </Count>
    </Row>
  );
};

export default LikeButton;
